import { IStyle } from "./../../../Types/Style";
import { Action as ReduxAction, handleActions } from "redux-actions";

import { ICompleteOrganization, IFontFamily, RequestState } from "../../types/common";
import { Action, IFirstFetchPayload, IPatchStylePayload, StyleLocation } from "../actions";

export interface IState {
  applyFontToSectionStatus: RequestState;
  styleId: string | null;
  style: IStyle | null;
  organization: ICompleteOrganization | null;
  addNewLogoError: { message: string } | null;
  addNewLogoStatus: RequestState;
  firstFetchStatus: RequestState;
  orgFontFamilies: IFontFamily[];
  loadOrgFontFamiliesState: RequestState;
  patchStyleError: string | null;
  patchStyleStatus: RequestState;
  patchStyleLocation: StyleLocation;
  patchOrganizationStatus: RequestState;
  patchOrganizationError: { message: string } | null;
}

const INITIAL_STATE: IState = {
  addNewLogoError: null,
  addNewLogoStatus: RequestState.IDLE,
  applyFontToSectionStatus: RequestState.IDLE,
  firstFetchStatus: RequestState.IDLE,
  loadOrgFontFamiliesState: RequestState.IDLE,
  orgFontFamilies: [],
  organization: null,
  patchOrganizationError: null,
  patchOrganizationStatus: RequestState.IDLE,
  patchStyleError: null,
  patchStyleLocation: null,
  patchStyleStatus: RequestState.IDLE,
  style: null,
  styleId: null,
};

const mount = (state: IState, action: ReduxAction<string>): IState => ({
  ...state,
  firstFetchStatus: RequestState.PENDING,
  styleId: action.payload,
});

const handleFirstFetchSuccess = (state: IState, action: ReduxAction<IFirstFetchPayload>): IState => ({
  ...state,
  firstFetchStatus: RequestState.SUCCEEDED,
  organization: action.payload.organization,
  style: action.payload.style,
});

const handleFirstFetchFailure = (state: IState): IState => ({
  ...state,
  firstFetchStatus: RequestState.FAILED,
  styleId: null,
});

const unmount = (_: IState): IState => INITIAL_STATE;

const patchStyle = (state: IState, action: ReduxAction<IPatchStylePayload>): IState => ({
  ...state,
  patchStyleError: null,
  patchStyleLocation: action.payload.location,
  patchStyleStatus: RequestState.PENDING,
});

const handlePatchStyleFailure = (state: IState, action: ReduxAction<string>): IState => ({
  ...state,
  patchStyleError: action.payload,
  patchStyleStatus: RequestState.FAILED,
});

const handlePatchStyleSuccess = (state: IState, action: ReduxAction<IStyle>): IState => ({
  ...state,
  patchStyleError: null,
  patchStyleStatus: RequestState.SUCCEEDED,
  style: action.payload,
});

const dismissPatchAlert = (state: IState): IState => ({
  ...state,
  patchStyleLocation: null,
  patchStyleStatus: RequestState.IDLE,
});

const addNewLogo = (state: IState): IState => ({
  ...state,
  addNewLogoError: null,
  addNewLogoStatus: RequestState.PENDING,
});

const handleAddNewLogoFailure = (state: IState, action: ReduxAction<{ message: string }>): IState => ({
  ...state,
  addNewLogoError: action.payload,
  addNewLogoStatus: RequestState.FAILED,
});

const handleAddNewLogoSuccess = (state: IState): IState => ({
  ...state,
  addNewLogoError: null,
  addNewLogoStatus: RequestState.SUCCEEDED,
});

const applyFontToSection = (state: IState): IState => ({
  ...state,
  applyFontToSectionStatus: RequestState.PENDING,
});

const handleApplyFontToSectionFailure = (state: IState): IState => ({
  ...state,
  applyFontToSectionStatus: RequestState.FAILED,
});

const handleApplyFontToSectionSuccess = (state: IState): IState => ({
  ...state,
  applyFontToSectionStatus: RequestState.SUCCEEDED,
});

const loadOrgFontFamilies = (state: IState): IState => ({
  ...state,
  loadOrgFontFamiliesState: RequestState.PENDING,
});

const handleLoadOrgFontFamiliesFailure = (state: IState): IState => ({
  ...state,
  loadOrgFontFamiliesState: RequestState.FAILED,
});

const handleLoadOrgFontFamiliesSuccess = (state: IState, action: ReduxAction<IFontFamily[]>): IState => ({
  ...state,
  loadOrgFontFamiliesState: RequestState.SUCCEEDED,
  orgFontFamilies: action.payload,
});

const patchOrganization = (state: IState): IState => ({
  ...state,
  patchOrganizationError: null,
  patchOrganizationStatus: RequestState.PENDING,
});

const handlePatchOrganizationSuccess = (state: IState, action: ReduxAction<ICompleteOrganization>): IState => ({
  ...state,
  organization: action.payload,
  patchOrganizationError: null,
  patchOrganizationStatus: RequestState.SUCCEEDED,
});

const handlePatchOrganizationFailure = (state: IState, action: ReduxAction<{ message: string }>): IState => ({
  ...state,
  patchOrganizationError: action.payload,
  patchOrganizationStatus: RequestState.FAILED,
});

type Payload =
  | IFirstFetchPayload
  | IStyle
  | IPatchStylePayload
  | ICompleteOrganization
  | { message: string }
  | string
  | void
  | IFontFamily[];

export default handleActions<IState, Payload>(
  {
    [Action.MOUNT]: mount,
    [Action.DISMISS_PATCH_ALERTS]: dismissPatchAlert,
    [Action.HANLDE_FIRST_FETCH_SUCCESS]: handleFirstFetchSuccess,
    [Action.HANLDE_FIRST_FETCH_FAILURE]: handleFirstFetchFailure,
    [Action.PATCH_STYLE]: patchStyle,
    [Action.HANLDE_PATCH_STYLE_SUCCESS]: handlePatchStyleSuccess,
    [Action.HANLDE_PATCH_STYLE_FAILURE]: handlePatchStyleFailure,
    [Action.ADD_NEW_LOGO]: addNewLogo,
    [Action.HANDLE_ADD_NEW_LOGO_FAILURE]: handleAddNewLogoFailure,
    [Action.HANDLE_ADD_NEW_LOGO_SUCCESS]: handleAddNewLogoSuccess,
    [Action.APPLY_FONT_TO_SECTION]: applyFontToSection,
    [Action.HANDLE_APPLY_FONT_TO_SECTION_FAILURE]: handleApplyFontToSectionFailure,
    [Action.HANDLE_APPLY_FONT_TO_SECTION_SUCCESS]: handleApplyFontToSectionSuccess,
    [Action.LOAD_ORG_FONT_FAMILIES]: loadOrgFontFamilies,
    [Action.HANDLE_LOAD_ORG_FONT_FAMILIES_FAILURE]: handleLoadOrgFontFamiliesFailure,
    [Action.HANDLE_LOAD_ORG_FONT_FAMILIES_SUCCESS]: handleLoadOrgFontFamiliesSuccess,
    [Action.PATCH_ORG]: patchOrganization,
    [Action.HANDLE_PATCH_ORG_SUCCESS]: handlePatchOrganizationSuccess,
    [Action.HANDLE_PATCH_ORG_FAILURE]: handlePatchOrganizationFailure,
    [Action.UNMOUNT]: unmount,
  },
  INITIAL_STATE
);
